import PromptService from "@Services/PromptService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@Constants";
import TermsService from "@Services/TermsService";
import { notification } from "antd";

export const useUpdateUserPrompt = () => {
    const promptService = new PromptService();
    const queryClient = useQueryClient();

    return useMutation(
        async ({ promptId, status }: { promptId: string; status: string }) =>
            await promptService.updateUserPrompt(promptId, status),
        {
            onSuccess: () => {
                queryClient.invalidateQueries([QueryKeys.UserPrompts]);
            },
        }
    );
};

export const useCreateTermsAction = () => {
    const termsService = new TermsService();
    const queryClient = useQueryClient();

    return useMutation(
        async ({ termsVersionId, accepted }: { termsVersionId: string; accepted: boolean }) =>
            await termsService.createTermsAction(termsVersionId, accepted),
        {
            onSuccess: (result, variables) => {
                if (!result.hasErrors) {
                    if (variables.accepted) {
                        notification.success({
                            message: "Terms accepted successfully.",
                        });
                    }
                    queryClient.invalidateQueries([QueryKeys.UserAcceptedTerms]);
                    queryClient.invalidateQueries([QueryKeys.UserPrompts]);
                } else {
                    if (variables.accepted) {
                        notification.error({
                            message: "Failed to accept terms",
                            description: "You may have already accepted these terms.",
                        });
                    }
                    throw new Error("Terms acceptance failed");
                }
            },
            onError: () => {
                throw new Error("Terms acceptance failed");
            },
        }
    );
};

export const useCreateTerms = () => {
    const termsService = new TermsService();
    const queryClient = useQueryClient();

    return useMutation(async (model: { termsType: string; content: string }) => await termsService.createTerms(model), {
        onSuccess: (result) => {
            if (!result.hasErrors) {
                queryClient.invalidateQueries([QueryKeys.Terms]);
                return result.value;
            } else {
                throw new Error(result.errors?.[0] || "Failed to create terms");
            }
        },
    });
};

export const useUpdateTerms = () => {
    const termsService = new TermsService();
    const queryClient = useQueryClient();

    return useMutation(
        async (model: { termsVersionId: string; termsType: string; content: string }) =>
            await termsService.updateTerms(model.termsVersionId, model),
        {
            onSuccess: () => queryClient.invalidateQueries([QueryKeys.Terms]),
        }
    );
};

export const usePublishTerms = () => {
    const termsService = new TermsService();
    const queryClient = useQueryClient();

    return useMutation(async (termsVersionId: string) => await termsService.publishTerms(termsVersionId), {
        onSuccess: () => {
            queryClient.invalidateQueries([QueryKeys.Terms]);
            queryClient.invalidateQueries([QueryKeys.UserAcceptedTerms]);
            queryClient.invalidateQueries([QueryKeys.UserPrompts]);
        },
    });
};
